<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3';
import { onKeyStroke } from '@vueuse/core';
import {
    BrainCircuit,
    CheckCircle,
    FileText,
    Trash2,
    Upload,
} from 'lucide-vue-next';
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue';
import { index as associationsIndex } from '@/actions/App/Http/Controllers/AssociationController';
import { signedUrl as fileSignedUrl } from '@/actions/App/Http/Controllers/FileController';
import {
    destroy as articleDestroy,
    index as articlesIndex,
} from '@/actions/App/Http/Controllers/PaperController';
import { store as referencesStore } from '@/actions/App/Http/Controllers/ReferencesController';
import ManualReferenceDialog from '@/components/references/ManualReferenceDialog.vue';
import type { ReferenceItem } from '@/components/references/ReferenceListItem.vue';
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue';
import RunProgressTracker from '@/components/shared/RunProgressTracker.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/composables/useToast';
import AppLayout from '@/layouts/AppLayout.vue';
import FileUploadZone from '@/pages/Files/FileUploadZone.vue';
import { importMethod as projectImportRoute } from '@/routes/projects';
import { screening as screeningRoute } from '@/routes/projects/articles';
import * as runsRoutes from '@/routes/runs';
import type { PaginatedResponse, Paper, Project } from '@/types';
import BibliographyTab from './partials/BibliographyTab.vue';

interface Props {
    project: Project;
    papers: PaginatedResponse<Paper> & {
        total_count: number;
        screening_counts: Record<string, number>;
    };
    references: ReferenceItem[];
    zotero: {
        connected: boolean;
        last_synced_at: string | null;
        collections: string[];
    };
    filters: {
        screening_decision?: string;
        source?: string;
        search?: string;
    };
    availableSources?: string[];
    active_run_id?: string | null;
    cited_paper_ids: string[];
    citation_style: string;
}

const props = defineProps<Props>();
const toast = useToast();

// Chargé à la demande : sort pdf.js du bundle initial.
const PdfViewer = defineAsyncComponent(
    () => import('@/components/shared/PdfViewer.vue'),
);
const screeningCounts = ref<Record<string, number>>({
    ...props.papers.screening_counts,
});

watch(
    () => props.papers.screening_counts,
    (counts) => {
        screeningCounts.value = { ...counts };
    },
    { deep: true },
);

// ─── Tab state ────────────────────────────────────────────────────────────────

const activeTab = ref<'articles' | 'bibliographie'>('articles');
const citedIdsSet = computed(() => new Set(props.cited_paper_ids));

const lastSynced = computed(() =>
    props.zotero.last_synced_at
        ? new Date(props.zotero.last_synced_at).toLocaleDateString('fr-FR', {
              dateStyle: 'medium',
          })
        : null,
);

defineOptions({
    layout: (props: { project: Project }) => [
        AppLayout,
        {
            breadcrumbs: [
                { title: 'Projets', href: '/projects' },
                {
                    title: props.project.name,
                    href: `/projects/${props.project.id}`,
                },
                { title: 'Articles', href: '#' },
            ],
        },
    ],
});

// ─── Filtres ───────────────────────────────────────────────────────────────────

const search = ref(props.filters.search || '');
const statusFilter = ref(props.filters.screening_decision || 'all');

const handleSearch = () => {
    router.get(
        articlesIndex.url(props.project.id),
        {
            search: search.value || undefined,
            screening_decision:
                statusFilter.value === 'all' ? undefined : statusFilter.value,
        },
        { preserveState: true, replace: true },
    );
};

watch([statusFilter], () => {
    handleSearch();
});

// ─── Filter chips config ───────────────────────────────────────────────────────

const filterChips = computed(() => [
    { value: 'all', label: 'Tous' },
    { value: 'included', label: 'À inclure' },
    { value: 'excluded', label: 'À exclure' },
    { value: 'maybe', label: 'À revoir' },
    { value: 'pending', label: 'Sans décision' },
]);

// ─── Decision helpers ─────────────────────────────────────────────────────────

const DECISION_LABELS: Record<string, string> = {
    included: 'À inclure',
    excluded: 'À exclure',
    maybe: 'À revoir',
};

const DECISION_CLASSES: Record<string, string> = {
    included:
        'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
    excluded: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
    maybe: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
};

function decisionLabel(decision: string | null | undefined): string {
    return DECISION_LABELS[decision ?? ''] ?? 'À lire';
}

function decisionClass(decision: string | null | undefined): string {
    return DECISION_CLASSES[decision ?? ''] ?? 'bg-muted text-muted-foreground';
}

// ─── Selected article for preview ─────────────────────────────────────────────

const selectedArticle = ref<Paper | null>(null);
const isPdfViewerOpen = ref(false);

const selectedArticlePdfs = computed(() => selectedArticle.value?.pdfs ?? []);

function selectArticle(paper: Paper) {
    selectedArticle.value = paper;
}

function openPdfFromList(paper: Paper) {
    selectedArticle.value = paper;
    isPdfViewerOpen.value = true;
}

type SuggestionPreview = { decision: string; reason?: string | null };

function firstSuggestion(paper: Paper): SuggestionPreview | null {
    if (paper.ai_suggestions?.[0]) {
        const s = paper.ai_suggestions[0];

        return { decision: s.decision, reason: s.rationale };
    }

    return paper.screening_suggestions?.[0] ?? null;
}

// ─── Suppression ──────────────────────────────────────────────────────────────

const isDeleteDialogOpen = ref(false);
const articleToDelete = ref<string | null>(null);

const confirmDelete = (id: string) => {
    articleToDelete.value = id;
    isDeleteDialogOpen.value = true;
};

const deleteArticle = () => {
    if (articleToDelete.value) {
        const deletedId = articleToDelete.value;
        router.delete(
            articleDestroy.url({
                project: props.project.id,
                article: deletedId,
            }),
            {
                onSuccess: () => {
                    isDeleteDialogOpen.value = false;
                    articleToDelete.value = null;

                    if (selectedArticle.value?.id === deletedId) {
                        selectedArticle.value = null;
                    }
                },
            },
        );
    }
};

// ─── Import PDF modal ─────────────────────────────────────────────────────────

const isPdfModalOpen = ref(false);

const handlePdfUploadsCompleted = () => {
    isPdfModalOpen.value = false;
    toast.success(
        'PDF ajouté. Vous pouvez le rattacher à un article depuis la page Associations.',
    );
    router.reload({ only: ['papers'] });
};

// ─── AI suggestions banner ────────────────────────────────────────────────────

const hasAiSuggestions = computed(() =>
    props.papers.data.some(
        (p) =>
            (p.ai_suggestions?.length ?? 0) > 0 ||
            (p.suggestions_count ?? 0) > 0,
    ),
);

// ─── Décision manuelle ───────────────────────────────────────────────────────

type ScreeningDecision = 'included' | 'excluded' | 'maybe';

const decisionSavingId = ref<string | null>(null);

async function setDecision(paper: Paper, decision: ScreeningDecision) {
    if (decisionSavingId.value === paper.id) {
        return;
    }

    decisionSavingId.value = paper.id;

    // Mise à jour optimiste locale
    const previousDecision = paper.screening_decision;
    const previousDecidedBy = paper.decided_by;
    paper.screening_decision = decision;
    paper.decided_by = 'human';

    if (selectedArticle.value?.id === paper.id) {
        selectedArticle.value = {
            ...selectedArticle.value,
            screening_decision: decision,
            decided_by: 'human',
        };
    }

    try {
        const csrfToken =
            (
                document.querySelector(
                    'meta[name="csrf-token"]',
                ) as HTMLMetaElement
            )?.content ?? '';
        const res = await fetch(
            screeningRoute.url({
                project: props.project.id,
                article: paper.id,
            }),
            {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                    Accept: 'application/json',
                },
                body: JSON.stringify({ screening_decision: decision }),
            },
        );

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            // Rollback
            paper.screening_decision = previousDecision;
            paper.decided_by = previousDecidedBy;

            if (selectedArticle.value?.id === paper.id) {
                selectedArticle.value = {
                    ...selectedArticle.value,
                    screening_decision: previousDecision,
                    decided_by: previousDecidedBy,
                };
            }

            toast.error(
                (err as { message?: string }).message ??
                    'Erreur lors de la sauvegarde de la décision.',
            );

            return;
        }

        const updated = (await res.json()) as Paper;

        if (selectedArticle.value?.id === paper.id) {
            selectedArticle.value = { ...selectedArticle.value, ...updated };
        }

        // Mise à jour locale des compteurs (évite un rechargement qui repositionne les articles)
        const oldKey = previousDecision ?? 'pending';
        const newKey = decision;

        if (oldKey in screeningCounts.value) {
            screeningCounts.value[oldKey]--;
        }

        if (newKey in screeningCounts.value) {
            screeningCounts.value[newKey]++;
        }
    } catch {
        paper.screening_decision = previousDecision;
        paper.decided_by = previousDecidedBy;

        if (selectedArticle.value?.id === paper.id) {
            selectedArticle.value = {
                ...selectedArticle.value,
                screening_decision: previousDecision,
                decided_by: previousDecidedBy,
            };
        }

        toast.error('Erreur réseau lors de la sauvegarde de la décision.');
    } finally {
        decisionSavingId.value = null;
    }
}

function reviewSuggestions() {
    const data = props.papers.data;
    // Priorité aux suggestions non encore tranchées, sinon la première suggestion.
    const target =
        data.find((p) => firstSuggestion(p) && !p.screening_decision) ??
        data.find((p) => firstSuggestion(p));

    if (target) {
        selectArticle(target);
    } else {
        toast.info('Aucune suggestion IA à revoir sur cette page.');
    }
}

async function acceptSuggestion(paper: Paper) {
    const currentIndex = props.papers.data.findIndex((p) => p.id === paper.id);
    const nextPaperId = props.papers.data[currentIndex + 1]?.id ?? null;
    const decision = firstSuggestion(paper)!.decision as ScreeningDecision;
    await setDecision(paper, decision);

    if (nextPaperId) {
        const nextPaper = props.papers.data.find((p) => p.id === nextPaperId);

        if (nextPaper) {
            selectArticle(nextPaper);
        }
    }
}

// ─── Navigation & raccourcis clavier ────────────────────────────────────────────

/** Sélectionne l'article à `offset` positions de l'article courant dans la liste. */
function selectRelative(offset: number) {
    const data = props.papers.data;

    if (data.length === 0) {
        return;
    }

    const currentIndex = selectedArticle.value
        ? data.findIndex((p) => p.id === selectedArticle.value!.id)
        : -1;

    let nextIndex =
        currentIndex === -1
            ? offset > 0
                ? 0
                : data.length - 1
            : currentIndex + offset;
    nextIndex = Math.max(0, Math.min(data.length - 1, nextIndex));

    const next = data[nextIndex];

    if (next) {
        selectArticle(next);
    }
}

/** Décide puis passe automatiquement à l'article suivant (flux de screening rapide). */
async function decideAndAdvance(paper: Paper, decision: ScreeningDecision) {
    await setDecision(paper, decision);
    selectRelative(1);
}

/** Vrai si le focus est dans un champ de saisie (on n'intercepte pas les raccourcis alors). */
function isTypingTarget(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;

    if (!target) {
        return false;
    }

    return (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
    );
}

function withShortcut(event: KeyboardEvent, handler: () => void) {
    if (activeTab.value !== 'articles' || isTypingTarget(event)) {
        return;
    }

    event.preventDefault();
    handler();
}

onKeyStroke('i', (e) =>
    withShortcut(e, () => {
        if (selectedArticle.value) {
            decideAndAdvance(selectedArticle.value, 'included');
        }
    }),
);
onKeyStroke('e', (e) =>
    withShortcut(e, () => {
        if (selectedArticle.value) {
            decideAndAdvance(selectedArticle.value, 'excluded');
        }
    }),
);
onKeyStroke('m', (e) =>
    withShortcut(e, () => {
        if (selectedArticle.value) {
            decideAndAdvance(selectedArticle.value, 'maybe');
        }
    }),
);
onKeyStroke('a', (e) =>
    withShortcut(e, () => {
        if (selectedArticle.value && firstSuggestion(selectedArticle.value)) {
            acceptSuggestion(selectedArticle.value);
        }
    }),
);
onKeyStroke(['j', 'ArrowDown'], (e) =>
    withShortcut(e, () => selectRelative(1)),
);
onKeyStroke(['k', 'ArrowUp'], (e) => withShortcut(e, () => selectRelative(-1)));

// ─── Screening run trigger ────────────────────────────────────────────────────

const screeningLoading = ref(false);
const screeningRunId = ref<string | null>(null);

function autoSelectFirstSuggestion(): void {
    if (selectedArticle.value) {
        return;
    }

    const withSuggestion = props.papers.data.find(
        (p) => (p.ai_suggestions?.length ?? 0) > 0,
    );
    selectedArticle.value = withSuggestion ?? props.papers.data[0] ?? null;
}

onMounted(() => {
    if (props.active_run_id) {
        screeningRunId.value = props.active_run_id;
    }

    autoSelectFirstSuggestion();
});

// Also trigger after Inertia reloads props (e.g. after AI screening completes)
watch(() => props.papers.data, autoSelectFirstSuggestion);

function onScreeningCompleted() {
    router.reload({ only: ['papers'] });
    toast.success('Screening IA terminé. Les décisions ont été mises à jour.');
    setTimeout(() => {
        screeningRunId.value = null;
    }, 2000);
}

function onScreeningFailed() {
    toast.error('Le screening IA a échoué.');
    screeningRunId.value = null;
}

function onScreeningCancelled() {
    screeningRunId.value = null;
    router.reload({ only: ['papers'] });
}

async function startScreening() {
    screeningLoading.value = true;

    try {
        const csrfToken =
            (
                document.querySelector(
                    'meta[name="csrf-token"]',
                ) as HTMLMetaElement
            )?.content ?? '';
        const res = await fetch(runsRoutes.store().url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
                Accept: 'application/json',
            },
            body: JSON.stringify({
                project_id: props.project.id,
                type: 'screening',
            }),
        });

        if (res.status === 409) {
            const data = (await res.json().catch(() => ({}))) as {
                message?: string;
                active_run_id?: string;
            };
            toast.info('Un screening est déjà en cours pour ce projet.');

            if (data.active_run_id) {
                screeningRunId.value = data.active_run_id;
            }

            return;
        }

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            toast.error(
                (err as { message?: string }).message ??
                    'Erreur lors du démarrage du screening.',
            );

            return;
        }

        const run = (await res.json()) as { id: string };
        screeningRunId.value = run.id;
        toast.success('Screening IA démarré. Traitement en cours…');
        router.reload({ only: ['papers'] });
    } catch {
        toast.error('Erreur réseau lors du démarrage du screening.');
    } finally {
        screeningLoading.value = false;
    }
}
</script>

<template>
    <Head title="Articles" />
    <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
        <!-- Tab toggle + actions header -->
        <div
            class="flex shrink-0 items-center justify-between border-b border-border/40 bg-background px-6 py-3"
        >
            <div
                class="flex w-fit gap-1 rounded-lg border border-border bg-muted/50 p-1"
            >
                <button
                    class="rounded-md px-3 py-1 text-sm font-medium transition-colors"
                    :class="
                        activeTab === 'articles'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                    "
                    @click="activeTab = 'articles'"
                >
                    Articles
                </button>
                <button
                    data-tour="bibliography-tab-btn"
                    class="rounded-md px-3 py-1 text-sm font-medium transition-colors"
                    :class="
                        activeTab === 'bibliographie'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                    "
                    @click="activeTab = 'bibliographie'"
                >
                    Bibliographie
                </button>
            </div>

            <div class="flex items-center gap-2">
                <template v-if="activeTab === 'articles'">
                    <Button
                        variant="default"
                        size="sm"
                        :disabled="screeningLoading || !!screeningRunId"
                        data-tour="ai-suggest-btn"
                        @click="startScreening"
                    >
                        <BrainCircuit class="mr-2 h-4 w-4" />
                        {{
                            screeningLoading
                                ? 'Démarrage…'
                                : screeningRunId
                                  ? 'Screening en cours…'
                                  : 'Lancer le screening IA'
                        }}
                    </Button>
                    <Dialog v-model:open="isPdfModalOpen">
                        <DialogTrigger as-child>
                            <Button variant="outline" size="sm">
                                <Upload class="mr-2 h-4 w-4" />
                                Ajouter un PDF
                            </Button>
                        </DialogTrigger>
                        <DialogContent class="max-w-lg">
                            <DialogHeader>
                                <DialogTitle>Ajouter un PDF</DialogTitle>
                                <DialogDescription>
                                    Le fichier sera ajouté à vos PDF. Vous
                                    pourrez ensuite le rattacher à un article
                                    depuis la page
                                    <strong>Associations</strong>.
                                </DialogDescription>
                            </DialogHeader>
                            <FileUploadZone
                                upload-url="/library/files"
                                @completed="handlePdfUploadsCompleted"
                            />
                        </DialogContent>
                    </Dialog>
                    <Button variant="outline" size="sm" as-child>
                        <Link :href="associationsIndex.url(project.id)">
                            Rattacher les PDF aux articles
                        </Link>
                    </Button>
                </template>
                <template v-else>
                    <div
                        v-if="zotero.connected"
                        class="flex items-center gap-2"
                    >
                        <Badge variant="secondary" class="gap-1">
                            <span
                                class="h-1.5 w-1.5 rounded-full bg-green-500"
                            />
                            Zotero connecté
                        </Badge>
                        <span
                            v-if="lastSynced"
                            class="text-xs text-muted-foreground"
                        >
                            Sync {{ lastSynced }}
                        </span>
                    </div>
                    <Badge v-else variant="outline">Zotero non connecté</Badge>
                    <ManualReferenceDialog
                        :project-id="project.id"
                        :store-url="referencesStore(project.id).url"
                    />
                </template>
            </div>
        </div>

        <!-- ── Articles split layout ──────────────────────────────────────── -->
        <div v-if="activeTab === 'articles'" class="flex min-h-0 flex-1">
            <!-- Left: list pane -->
            <section
                class="flex w-96 shrink-0 flex-col overflow-hidden border-r border-border/60"
                data-tour="articles-list"
            >
                <!-- Toolbar -->
                <div class="shrink-0 space-y-3 border-b border-border/60 p-4">
                    <Input
                        v-model="search"
                        type="search"
                        placeholder="Rechercher un article…"
                        class="h-9 text-sm"
                        @keydown.enter="handleSearch"
                    />
                    <div class="flex flex-wrap gap-1.5">
                        <button
                            v-for="chip in filterChips"
                            :key="chip.value"
                            class="rounded-full border px-3 py-0.5 text-xs font-medium transition-colors"
                            :class="
                                statusFilter === chip.value
                                    ? 'border-foreground bg-foreground text-background'
                                    : 'border-border text-muted-foreground hover:border-foreground hover:text-foreground'
                            "
                            @click="statusFilter = chip.value"
                        >
                            {{ chip.label }}
                            <span class="ml-1 opacity-60">
                                <template v-if="chip.value === 'all'">{{
                                    papers.total_count
                                }}</template>
                                <template v-else>{{
                                    screeningCounts[chip.value] ?? 0
                                }}</template>
                            </span>
                        </button>
                    </div>
                    <Link
                        :href="projectImportRoute(props.project.id).url"
                        class="block text-xs text-muted-foreground transition-colors hover:text-foreground"
                    >
                        + Importer des articles
                    </Link>
                </div>

                <!-- AI banner -->
                <div
                    v-if="hasAiSuggestions"
                    class="flex shrink-0 items-start gap-3 border-b border-border/60 bg-brand/5 p-4 dark:bg-brand/10"
                >
                    <span
                        class="shrink-0 rounded bg-brand px-1.5 py-0.5 text-[10px] font-semibold text-white"
                    >
                        IA
                    </span>
                    <div class="min-w-0 flex-1 text-xs leading-snug">
                        <strong>Suggestions de sélection</strong> — l'assistant
                        a proposé des décisions pour certains articles.
                    </div>
                    <button
                        type="button"
                        class="shrink-0 text-xs text-muted-foreground underline hover:text-foreground"
                        @click="reviewSuggestions"
                    >
                        Tout revoir
                    </button>
                </div>

                <!-- Screening progress tracker -->
                <RunProgressTracker
                    v-if="screeningRunId"
                    :run-id="screeningRunId"
                    feature-label="Screening IA en cours…"
                    @completed="onScreeningCompleted"
                    @failed="onScreeningFailed"
                    @cancelled="onScreeningCancelled"
                />

                <!-- Article list -->
                <ul class="flex-1 divide-y divide-border/40 overflow-y-auto">
                    <li
                        v-for="paper in papers.data"
                        :key="paper.id"
                        class="cursor-pointer px-4 py-3 transition-colors hover:bg-muted/40"
                        :class="
                            selectedArticle?.id === paper.id
                                ? 'bg-accent/60'
                                : ''
                        "
                        @click="selectArticle(paper)"
                    >
                        <!-- Title + decision -->
                        <div class="flex items-start justify-between gap-2">
                            <span
                                class="line-clamp-2 text-sm leading-snug font-medium"
                            >
                                {{ paper.title }}
                            </span>
                            <span
                                v-if="paper.screening_decision"
                                class="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                                :class="decisionClass(paper.screening_decision)"
                            >
                                {{ decisionLabel(paper.screening_decision) }}
                            </span>
                        </div>
                        <!-- Meta -->
                        <div
                            class="mt-1 truncate text-xs text-muted-foreground"
                        >
                            {{
                                Array.isArray(paper.authors)
                                    ? paper.authors.slice(0, 2).join(', ')
                                    : paper.authors
                            }}
                            <template v-if="paper.journal">
                                · {{ paper.journal }}</template
                            >
                            <template v-if="paper.year">
                                · {{ paper.year }}</template
                            >
                        </div>
                        <!-- Chips -->
                        <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <span
                                v-if="
                                    (paper.ai_suggestions?.length ?? 0) > 0 ||
                                    (paper.suggestions_count ?? 0) > 0
                                "
                                class="rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-medium text-brand dark:bg-brand/20"
                            >
                                Suggestion IA
                            </span>
                            <span
                                v-if="paper.source"
                                class="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground"
                            >
                                {{ paper.source }}
                            </span>
                            <!-- PDF associé : bouton cliquable pour ouvrir le lecteur -->
                            <button
                                v-if="(paper.pdfs?.length ?? 0) > 0"
                                type="button"
                                class="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700 transition-colors hover:bg-green-200 dark:bg-green-950/50 dark:text-green-400 dark:hover:bg-green-900/60"
                                @click.stop="openPdfFromList(paper)"
                            >
                                <FileText class="h-3 w-3" />
                                PDF
                            </button>
                            <!-- PDF manquant : indicateur statique -->
                            <span
                                v-else-if="
                                    paper.pdfs !== undefined &&
                                    paper.pdfs !== null
                                "
                                class="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-950/50 dark:text-amber-400"
                            >
                                PDF manquant
                            </span>
                        </div>
                    </li>

                    <li
                        v-if="papers.data.length === 0"
                        class="px-4 py-10 text-center text-sm text-muted-foreground"
                    >
                        Aucun article ne correspond aux filtres.
                    </li>
                </ul>
            </section>

            <!-- Right: preview pane -->
            <aside
                class="flex-1 overflow-y-auto"
                data-tour="articles-screening"
            >
                <!-- Empty state -->
                <div
                    v-if="!selectedArticle"
                    class="flex h-full items-center justify-center text-muted-foreground"
                >
                    <p class="text-sm">
                        Sélectionnez un article pour voir le détail
                    </p>
                </div>

                <div v-else class="mx-auto max-w-2xl space-y-6 p-6">
                    <!-- Preview header -->
                    <div class="space-y-2">
                        <div
                            class="text-xs font-semibold tracking-wider text-muted-foreground uppercase"
                        >
                            Article sélectionné
                        </div>
                        <h2 class="text-xl leading-snug font-semibold">
                            {{ selectedArticle.title }}
                        </h2>
                        <p class="text-sm text-muted-foreground">
                            {{
                                Array.isArray(selectedArticle.authors)
                                    ? selectedArticle.authors.join(', ')
                                    : selectedArticle.authors
                            }}
                        </p>
                        <p class="text-sm text-muted-foreground">
                            <template v-if="selectedArticle.journal">{{
                                selectedArticle.journal
                            }}</template>
                            <template v-if="selectedArticle.year">
                                · {{ selectedArticle.year }}</template
                            >
                            <template v-if="selectedArticle.doi">
                                · doi: {{ selectedArticle.doi }}</template
                            >
                        </p>
                        <div class="flex flex-wrap gap-2">
                            <span
                                v-if="selectedArticle.screening_decision"
                                class="rounded-full px-2.5 py-1 text-xs font-semibold"
                                :class="
                                    decisionClass(
                                        selectedArticle.screening_decision,
                                    )
                                "
                            >
                                {{
                                    decisionLabel(
                                        selectedArticle.screening_decision,
                                    )
                                }}
                            </span>
                            <span
                                v-if="selectedArticle.source"
                                class="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground"
                            >
                                {{ selectedArticle.source }}
                            </span>
                            <!-- PDF badge in detail panel -->
                            <button
                                v-if="(selectedArticle.pdfs?.length ?? 0) > 0"
                                type="button"
                                class="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700 transition-colors hover:bg-green-200 dark:bg-green-950/50 dark:text-green-400 dark:hover:bg-green-900/60"
                                @click="isPdfViewerOpen = true"
                            >
                                <FileText class="h-3.5 w-3.5" />
                                Lire le PDF
                            </button>
                            <span
                                v-else-if="
                                    selectedArticle.pdfs !== undefined &&
                                    selectedArticle.pdfs !== null
                                "
                                class="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700 dark:bg-amber-950/50 dark:text-amber-400"
                            >
                                PDF manquant
                            </span>
                        </div>
                    </div>

                    <!-- AI card -->
                    <div
                        v-if="firstSuggestion(selectedArticle)"
                        class="space-y-3 rounded-xl border border-brand/20 bg-brand/5 p-4 dark:border-brand/30 dark:bg-brand/10"
                    >
                        <div class="flex items-center gap-2">
                            <span
                                class="rounded bg-brand px-1.5 py-0.5 text-xs font-semibold text-white"
                            >
                                Suggestion de l'assistant
                            </span>
                        </div>
                        <div class="text-sm">
                            Décision proposée :
                            <strong>{{
                                decisionLabel(
                                    firstSuggestion(selectedArticle)!.decision,
                                )
                            }}</strong>
                        </div>
                        <div
                            v-if="firstSuggestion(selectedArticle)!.reason"
                            class="space-y-1"
                        >
                            <div
                                class="text-xs font-semibold tracking-wider text-muted-foreground uppercase"
                            >
                                Raisonnement
                            </div>
                            <p
                                class="text-sm leading-relaxed text-foreground/80"
                            >
                                {{ firstSuggestion(selectedArticle)!.reason }}
                            </p>
                        </div>
                        <div class="flex gap-2 pt-1">
                            <Button
                                size="sm"
                                :disabled="
                                    decisionSavingId === selectedArticle.id
                                "
                                @click="acceptSuggestion(selectedArticle)"
                            >
                                Accepter la suggestion
                            </Button>
                        </div>
                    </div>

                    <!-- Décision manuelle -->
                    <div class="space-y-3 rounded-xl border border-border p-4">
                        <div class="flex items-center justify-between">
                            <div class="text-sm font-semibold">
                                Décision de screening
                            </div>
                            <span
                                v-if="selectedArticle.decided_by === 'ai'"
                                class="rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-medium text-brand dark:bg-brand/20"
                            >
                                Suggérée par l'IA
                            </span>
                            <span
                                v-else-if="
                                    selectedArticle.decided_by === 'human' ||
                                    selectedArticle.decided_by === 'manual'
                                "
                                class="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                            >
                                Décision manuelle
                            </span>
                        </div>
                        <div class="flex flex-wrap gap-2">
                            <Button
                                size="sm"
                                :variant="
                                    selectedArticle.screening_decision ===
                                    'included'
                                        ? 'default'
                                        : 'outline'
                                "
                                :disabled="
                                    decisionSavingId === selectedArticle.id
                                "
                                class="border-green-600"
                                :class="
                                    selectedArticle.screening_decision ===
                                    'included'
                                        ? 'bg-green-600 text-white hover:bg-green-700'
                                        : 'text-green-700 hover:bg-green-50 hover:text-green-800 dark:text-green-400 dark:hover:bg-green-950/40 dark:hover:text-green-300'
                                "
                                @click="
                                    decideAndAdvance(
                                        selectedArticle,
                                        'included',
                                    )
                                "
                            >
                                <CheckCircle
                                    v-if="
                                        selectedArticle.screening_decision ===
                                        'included'
                                    "
                                    class="mr-1.5 h-3.5 w-3.5"
                                />
                                À inclure
                            </Button>
                            <Button
                                size="sm"
                                :variant="
                                    selectedArticle.screening_decision ===
                                    'excluded'
                                        ? 'default'
                                        : 'outline'
                                "
                                :disabled="
                                    decisionSavingId === selectedArticle.id
                                "
                                class="border-red-500"
                                :class="
                                    selectedArticle.screening_decision ===
                                    'excluded'
                                        ? 'bg-red-600 text-white hover:bg-red-700'
                                        : 'text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950/40 dark:hover:text-red-300'
                                "
                                @click="
                                    decideAndAdvance(
                                        selectedArticle,
                                        'excluded',
                                    )
                                "
                            >
                                <CheckCircle
                                    v-if="
                                        selectedArticle.screening_decision ===
                                        'excluded'
                                    "
                                    class="mr-1.5 h-3.5 w-3.5"
                                />
                                À exclure
                            </Button>
                            <Button
                                size="sm"
                                :variant="
                                    selectedArticle.screening_decision ===
                                    'maybe'
                                        ? 'default'
                                        : 'outline'
                                "
                                :disabled="
                                    decisionSavingId === selectedArticle.id
                                "
                                class="border-amber-500"
                                :class="
                                    selectedArticle.screening_decision ===
                                    'maybe'
                                        ? 'bg-amber-500 text-white hover:bg-amber-600'
                                        : 'text-amber-600 hover:bg-amber-50 hover:text-amber-700 dark:text-amber-400 dark:hover:bg-amber-950/40 dark:hover:text-amber-300'
                                "
                                @click="
                                    decideAndAdvance(selectedArticle, 'maybe')
                                "
                            >
                                <CheckCircle
                                    v-if="
                                        selectedArticle.screening_decision ===
                                        'maybe'
                                    "
                                    class="mr-1.5 h-3.5 w-3.5"
                                />
                                À revoir
                            </Button>
                        </div>
                        <!-- Légende des raccourcis clavier -->
                        <div
                            class="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground"
                        >
                            <span class="inline-flex items-center gap-1">
                                <kbd
                                    class="rounded border border-border bg-muted px-1 font-mono"
                                    >I</kbd
                                >
                                inclure
                            </span>
                            <span class="inline-flex items-center gap-1">
                                <kbd
                                    class="rounded border border-border bg-muted px-1 font-mono"
                                    >E</kbd
                                >
                                exclure
                            </span>
                            <span class="inline-flex items-center gap-1">
                                <kbd
                                    class="rounded border border-border bg-muted px-1 font-mono"
                                    >M</kbd
                                >
                                revoir
                            </span>
                            <span class="inline-flex items-center gap-1">
                                <kbd
                                    class="rounded border border-border bg-muted px-1 font-mono"
                                    >A</kbd
                                >
                                accepter l'IA
                            </span>
                            <span class="inline-flex items-center gap-1">
                                <kbd
                                    class="rounded border border-border bg-muted px-1 font-mono"
                                    >J</kbd
                                >/<kbd
                                    class="rounded border border-border bg-muted px-1 font-mono"
                                    >K</kbd
                                >
                                naviguer
                            </span>
                        </div>
                    </div>

                    <!-- Résumé -->
                    <div class="space-y-2">
                        <div class="text-sm font-semibold">
                            Résumé de l'article
                        </div>
                        <p
                            v-if="selectedArticle.abstract"
                            class="text-sm leading-relaxed text-muted-foreground"
                        >
                            {{ selectedArticle.abstract }}
                        </p>
                        <p v-else class="text-sm text-muted-foreground italic">
                            Aucun résumé disponible.
                        </p>
                    </div>

                    <!-- Actions -->
                    <div
                        class="flex flex-wrap items-center gap-3 border-t border-border pt-4"
                    >
                        <Button
                            variant="ghost"
                            size="sm"
                            class="ml-auto text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                            @click="confirmDelete(selectedArticle.id)"
                        >
                            <Trash2 class="mr-2 h-4 w-4" />
                            Retirer du projet
                        </Button>
                    </div>
                </div>
            </aside>
        </div>

        <!-- ── Bibliographie tab ───────────────────────────────────── -->
        <BibliographyTab
            v-else
            :project="project"
            :references="references"
            :zotero="zotero"
            :cited-ids="citedIdsSet"
            :cited-order="cited_paper_ids"
            :citation-style="citation_style"
        />

        <ConfirmDialog
            :open="isDeleteDialogOpen"
            @update:open="isDeleteDialogOpen = $event"
            title="Retirer cet article du projet ?"
            description="Cet article et son historique de statut seront supprimés. Cette action est irréversible."
            confirm-text="Retirer"
            @confirm="deleteArticle"
        />

        <PdfViewer
            v-if="selectedArticle"
            :open="isPdfViewerOpen"
            :pdfs="selectedArticlePdfs"
            :resolve-url="
                (fileId: string) =>
                    fileSignedUrl.url({
                        project: project.id,
                        article: selectedArticle!.id,
                        file: fileId,
                    })
            "
            @update:open="isPdfViewerOpen = $event"
        />
    </div>
</template>
